import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { MatDialog as MatDialog } from '@angular/material/dialog';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Ability, subject } from '@casl/ability';
import * as fromRoot from '../../../../../../../../../../state/app.state';
import { DailyReportService } from '../../../../daily-report.service';
import { ProjectsService } from '../../../../../../../../core/projects.service';
import { CommonService } from '../../../../../../../../../../shared/services/common.service';
import { ActivatedRoute } from '@angular/router';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { dailyReport, projectDetails } from '../../../../../../../../core/projects.selectors';
import { exprEvalConfig, offsetTypes } from '../../../../../../../../../../shared/core/shared.selectors';
import { StationComponent } from '../station/station.component';
import { ExpressionEvaluatorService } from '../../../../../../../../../../shared/services/expression-evaluator.service';
import { clearLocationControls, patchPickedLocation, hasRecordLocation } from '../../../../../../../../../../shared/helpers/design-context';
import { SignatureComponent } from '../../control-forms-details/signature/signature.component';
import {
  buildSignatureRoleOptions,
  buildSignatureView,
  mergeSignatureEmission,
  SignatureView,
} from '../../control-forms-details/control-forms-details/control-forms-details.component';

@Component({
    selector: 'app-testing-from',
    templateUrl: './testing-from.component.html',
    styleUrls: ['./testing-from.component.scss'],
    standalone: false
})
export class TestingFromComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() data: any;
  // `preview: true` is set by Settings > Forms, which renders a BLANK instance of
  // a form template purely to show what it looks like. In that mode the form-type
  // dropdown is hidden (the template is already chosen) and the template may come
  // from another project — see resolveFormTemplate().
  @Input() options: { preview?: boolean } = {};

  get isPreview(): boolean {
    return !!this.options?.preview;
  }

  /**
   * Dialogs open as full-page overlays, which in the Settings > Forms preview
   * escape the phone frame entirely and invite filling in a form that is never
   * saved. Preview keeps the buttons; tapping them does nothing.
   */
  // In preview the signature sheet is drawn INSIDE the phone frame — the field
  // app pushes its own screen, and a web dialog would break out of the phone.
  @Output() previewSignature = new EventEmitter<void>();

  private get dialogsSuppressed(): boolean {
    return this.isPreview;
  }

  /**
   * Resolves a form template by id. Prefers this project's store-backed
   * `fromTypeOptions` (the daily-report editor's source of truth), and falls back
   * to the template carried on the record itself — which is how Settings > Forms
   * can preview a form that belongs to a DIFFERENT project and so is absent from
   * this project's options. Mirrors the same fallback in control-forms-details.
   */
  private resolveFormTemplate(controlFormId: any): any {
    const storeTemplate = this.fromTypeOptions?.find(o => o.id === controlFormId);
    if (storeTemplate) {
      return storeTemplate;
    }
    return this.data?.control_form?.id === controlFormId ? this.data.control_form : null;
  }

  errorMessages: any = {};
  errorMessageMap = {
    formType: {
      required: 'This field is required.',
    },
    frequency: {
      required: 'This field is required.',
    },
  };
  form = new FormGroup<any>({
    formType: new FormControl({ value: '', disabled: false }, [Validators.required]),
    latitude: new FormControl({ value: null, disabled: false }),
    longitude: new FormControl({ value: null, disabled: false }),
    comment: new FormControl({ value: null, disabled: false }),
    pictures: new FormControl({ value: JSON.stringify([]), disabled: false }),
    videos: new FormControl({ value: JSON.stringify([]), disabled: false }),
    map_feature_link: new FormControl(null),
  });

  uiConfig: any;
  fromTypeOptions: any[] = [];
  fields = [];
  lines = [];
  displayCommentSection = false;
  mediaFiles = [];
  videoToBeDeletedFromMuxDb = [];
  controlForms: any;
  dailyReport: any;
  // Add/delete affordances follow the DailyReport CASL grant (same check the API
  // makes on save). Preview renders a blank template and keeps its buttons.
  editable = false;
  responseOptions: any;
  stations: any = [];
  project: any = {};
  alignment: any;
  expandedSection: any;
  headerFields: any = [];
  footerFields: any = [];
  lineDetails: any = [];
  stationBlockFields: any = [];
  timeBlockFields: any = [];
  picklistOptions = {};
  exprEvalConfigExpressions: any;
  offsetTypeOptions: any = [];
  stationLineKeys: any = [];
  timeKeys: any = [];
  selectedStationId: any;
  stationFormGroup = new FormGroup<any>({});
  timeFormGroup = new FormGroup<any>({});
  lineFormGroup = new FormGroup<any>({});
  selectedTimeBlockId: any;
  averageField: any;
  stationAverageFieldMap: {};
  selectedMediaFiles: any = [];
  enableNestedAccordion: boolean;
  lineKeys: any = [];
  selectedLineId: any;
  lineFields: any = [];
  initialFormKeys: any[];
  // Gated on the form template's ui_config.display_signatures. When true, the
  // SIGNATURE section (saved signers + Add Signature) renders. Additive: forms
  // without display_signatures are unchanged.
  displaySignature = false;
  // Dynamic list of captured signers (arbitrary count; roles may repeat).
  // Mirrors the control-forms-details signatures[] contract so the shared
  // signature dialog + serializer work unchanged.
  signatures: any = [];
  // Read-only presentation models derived from `signatures` (see buildSignatureView).
  signatureViews: SignatureView[] = [];
  protected readonly Object = Object;
  protected readonly name = name;
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private store: Store<fromRoot.State>,
    private dailyReportService: DailyReportService,
    private projectsService: ProjectsService,
    private commonService: CommonService,
    private cdRef: ChangeDetectorRef,
    public dialog: MatDialog,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private evaluator: ExpressionEvaluatorService,
    private ability: Ability,
  ) {
  }

  ngOnInit() {
    this.initialFormKeys = Object.keys(this.form.controls);

    this.route.queryParams
      .pipe(takeUntil(this.onDestroy))
      .subscribe(qp => {
        if (qp && qp['expandedSection']) {
          this.expandedSection = qp['expandedSection'];
        }
      });

    this.store.select(projectDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(project => {
        this.project = project;
        if (project?.control_forms?.length) {
          const { control_forms } = project;
          this.fromTypeOptions = control_forms.filter((form: any) => form.display_widget === 'testing_forms');
        } else {
          this.fromTypeOptions = [];
        }
      });

    this.store.select(exprEvalConfig)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((eec: any) => {
        this.exprEvalConfigExpressions = eec.expressions;
      });

    this.store.select(dailyReport)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((report: any) => {
        this.dailyReport = JSON.parse(JSON.stringify(report));
        this.controlForms = this.dailyReport?.reportDetails?.controlForms?.testingForms || [];
        this.editable = this.isPreview || this.ability.can('create', subject('DailyReport', { ...report }));
      });

    this.store.select(offsetTypes)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.offsetTypeOptions = data;
      });

    this.commonService.uploadedFilesInfo
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        const form: FormGroup = this.getForm(
          this.enableNestedAccordion,
          this.selectedStationId,
          this.selectedTimeBlockId,
          this.selectedLineId,
        );

        const items = Array.isArray(data) ? data : [data];

        const isNested = this.enableNestedAccordion
          ? !!(this.selectedStationId && this.selectedTimeBlockId)
          : !!this.selectedLineId;

        let targetPropertyName: 'selectedMediaFiles' | 'mediaFiles';

        if (isNested) {
          targetPropertyName = 'selectedMediaFiles';
        } else {
          targetPropertyName = 'mediaFiles';
        }

        const mediaArray = isNested ? this.selectedMediaFiles : this.mediaFiles;

        items.forEach((eachItem: any) => {
          const fileType = eachItem?.fileType;
          if (!fileType) {
            return;
          }

          if (fileType === 'picture') {
            const pictures = this.getPictures(form);
            pictures.push(eachItem);
            form.get('pictures')?.setValue(JSON.stringify(pictures));
          } else if (fileType === 'video') {
            const videos = this.getVideos(form);
            videos.push(eachItem);
            form.get('videos')?.setValue(JSON.stringify(videos));
          }

          mediaArray.push(eachItem);
        });

        if (targetPropertyName === 'selectedMediaFiles') {
          this.selectedMediaFiles = [...this.selectedMediaFiles];
        } else {
          this.mediaFiles = [...this.mediaFiles];
        }
      });

    this.dailyReportService.selectedLocation
      .pipe(takeUntil(this.onDestroy))
      .subscribe((location) => {
        // Only the keys the picker sent: an absent link key leaves the link untouched.
        patchPickedLocation(this.form, location);
      });

    this.dailyReportService.addComment
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.displayCommentSection = true;
      });

    this.dailyReportService.triggerSave
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.dailyReportService.formatTestingForms(
          this.form,
          this.headerFields,
          this.footerFields,
          this.lineDetails,
          this.data,
          this.enableNestedAccordion,
          this.signatures,
        );
      });

    this.dailyReportService.setSignature
      .pipe(takeUntil(this.onDestroy))
      .subscribe((emission) => {
        // Same channel + emission shape as control-forms-details: `index`
        // targets an existing signer slot; a null/out-of-range index appends a
        // new signer (roles may repeat). `value` maps to signature_text.
        const idx = emission?.index;
        const isExisting = typeof idx === 'number' && idx >= 0 && idx < this.signatures.length;
        if (isExisting) {
          this.signatures[idx] = mergeSignatureEmission(this.signatures[idx], emission);
        } else {
          this.signatures.push(mergeSignatureEmission(null, emission));
        }

        this.refreshSignatureViews();
        this.cdRef.detectChanges();
      });

    this.dailyReportService.triggerDelete
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.dailyReportService.delete.next({
          type: 'forms',
          id: this.data?.id,
        });
      });

    this.dailyReportService.add
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.openStationPopup();
      });

    this.form?.controls?.['formType']?.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        this.lineKeys = [];
        this.selectedMediaFiles = [];
        this.mediaFiles = [];
        this.resetToInitialState();
        const formTypeDetails = this.resolveFormTemplate(data);
        this.uiConfig = formTypeDetails?.ui_config;

        this.displaySignature = !!this.uiConfig?.display_signatures;
        this.headerFields = this.uiConfig?.header?.fields || [];
        this.footerFields = this.uiConfig?.footer?.fields || [];
        this.lineDetails = this.uiConfig?.lines;
        this.enableNestedAccordion = !!(this.uiConfig?.show_station && this.uiConfig?.show_time);

        if (!this.enableNestedAccordion) {
          this.lineFields = this.lineDetails?.fields || [];
        }

        this.averageField = this.lineDetails?.fields?.find(o => o.name === this.lineDetails?.summary?.average_field);

        const fields = this.lineDetails?.fields;
        this.stationBlockFields = fields?.filter(field => this.lineDetails?.group_by?.includes(field?.name));
        this.timeBlockFields = fields?.filter(field => !this.lineDetails?.group_by?.includes(field?.name));

        this.createPicklistOptions();

        this.headerFields.forEach((field: any) => {
          if (field.type === 'time') {
            this.form?.addControl(
              field?.name,
              new FormControl({ value: null, disabled: !!field?.display_only },
                field?.is_optional === false ? [Validators.required] : [],
              ),
            );

            this.form?.addControl(
              `${field.name}_timePeriod`,
              new FormControl({ value: null, disabled: false }),
            );

            this.form?.addControl(
              `${field.name}_am_pm`,
              new FormControl({ value: null, disabled: false }),
            );
          } else {
            this.form?.addControl(
              field?.name,
              new FormControl({ value: null, disabled: !!field?.display_only },
                field?.is_optional === false ? [Validators.required] : [],
              ),
            );
          }
        });

        this.headerFields.forEach((field: any) => {
          if (field?.dependent_fields && field?.dependent_fields?.length) {
            this.form.controls[field?.name].valueChanges
              .pipe(takeUntil(this.onDestroy))
              .subscribe((d) => {
                if (d) {
                  const options = this.picklistOptions[field?.name];
                  const selectedOption = options?.find(o => o.value === d);
                  const temp = selectedOption?.extra_attributes;

                  Object.entries(temp).forEach(([key, value]: any) => {
                    field?.dependent_fields?.forEach(o => {
                      if (o?.extra_attribute === key) {
                        const controlKey = o.name;
                        const control = this.form.get(controlKey);
                        if (control) {
                          control.setValue(value, { emitEvent: true });
                        }
                      }
                    });
                  });

                }
              });
          }

          if (field?.allow_other) {
            this.form.controls[field.name].valueChanges
              .pipe(takeUntil(this.onDestroy))
              .subscribe(d => {
                if (d === '_others') {
                  this.form.addControl(
                    `${field.name}_others`,
                    new FormControl({ value: null, disabled: false }),
                  );
                } else {
                  this.form.removeControl(`${field.name}_others`);
                }
              });
          }
        });

        this.footerFields.forEach((field: any) => {
          this.form?.addControl(
            field?.name,
            new FormControl({ value: null, disabled: !!field?.display_only },
              field?.is_optional === false ? [Validators.required] : [],
            ),
          );
        });
      });

    this.dailyReportService.stationAdded
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        this.stationLineKeys = this.getStationLineKeys() || [];
        this.selectStation(data);
        const timeKey = Object.keys(this.stationFormGroup.controls).find(key => key.includes('time_'));
        if (timeKey) {
          this.selectTimeBlock(timeKey);
        }
      });

    this.form.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.projectsService.isAnyFormDirty.next(true);
      });

    if (this.data) {
      this.rebuildFormFromSavedData();
    }
  }

  ngAfterViewInit() {
    this.projectsService.isAnyFormDirty.next(false);
  }

  checkForErrors(currentField?: string) {
    this.errorMessages = {
      ...this.errorMessages,
      ...(this.commonService.checkFormValidation(this.form, this.errorMessageMap, currentField)),
    };
  }

  deletePicture(pic) {
    const form: any = this.getForm(this.enableNestedAccordion, this.selectedStationId, this.selectedTimeBlockId, this.selectedLineId);

    if (pic.fileType === 'picture') {
      let pictures = this.getPictures(form);

      pictures = pictures?.filter(o => o.id !== pic.id);

      const isNested = this.enableNestedAccordion
        ? !!(this.selectedStationId && this.selectedTimeBlockId)
        : !!this.selectedLineId;

      if (isNested) {
        this.selectedMediaFiles = this.selectedMediaFiles?.filter(o => o.id !== pic.id);
      } else {
        this.mediaFiles = this.mediaFiles?.filter(o => o.id !== pic.id);
      }

      form?.get('pictures')?.setValue(JSON.stringify(pictures));
    }

    if (pic.fileType === 'video') {
      let videos = this.getVideos(form);

      videos = videos?.filter(o => o.id !== pic.id);

      const isNested = this.enableNestedAccordion
        ? !!(this.selectedStationId && this.selectedTimeBlockId)
        : !!this.selectedLineId;

      if (isNested) {
        this.selectedMediaFiles = this.selectedMediaFiles?.filter(o => o.id !== pic.id);
      } else {
        this.mediaFiles = this.mediaFiles?.filter(o => o.id !== pic.id);
      }

      form?.get('videos')?.setValue(JSON.stringify(videos));
      this.videoToBeDeletedFromMuxDb.push(pic.id);
    }
  }

  deleteLocation() {
    clearLocationControls(this.form);
  }

  showLocationSection(): boolean {
    const hasGps = this.form?.controls?.['latitude']?.value !== null &&
      this.form?.controls?.['longitude']?.value !== null &&
      this.form?.controls?.['latitude']?.value !== '' &&
      this.form?.controls?.['longitude']?.value !== '';
    // W50: a sheet-only drawing link (no pin) is the location too; the shared card shows its strip
    return hasRecordLocation({ hasGps, link: this.form?.controls?.['map_feature_link']?.value?.sheet ? this.form.controls['map_feature_link'].value : null });
  }

  getPictures(form): any[] {
    try {
      const pictures = form.controls['pictures']?.value;
      return JSON.parse(pictures) || [];
    } catch (e) {
      return [];
    }
  }

  getVideos(form): any[] {
    try {
      const videos = form.controls['videos']?.value;
      return JSON.parse(videos) || [];
    } catch (e) {
      return [];
    }
  }

  createPicklistOptions() {
    if (!this.project) {
      return;
    }

    const allFields = [
      ...(this.headerFields ?? []),
      ...(this.footerFields ?? []),
    ];

    const options: Record<string, any[]> = { ...this.picklistOptions };

    allFields.forEach(o => {
      const [key, labelToUse] = o?.pick_list?.split?.('.') ?? [];

      if (!key) {
        return;
      }

      if (key === 'activity_codes') {
        options[o.name] = this.project?.activity_codes_forms ?? [];
      } else {
        const sourceArray = this.project?.[key] ?? [];
        options[o.name] = sourceArray.map(item => ({
          ...item,
          label: item?.[labelToUse] ?? '',
        }));
      }
    });

    this.picklistOptions = options;
  }

  openStationPopup() {
    if (this.dialogsSuppressed) {
      return;
    }

    this.dialog.open(StationComponent, {
      disableClose: false,
      width: '580px',
      height: '644px',
      data: {
        form: this.form,
        stationFormFields: this.stationBlockFields,
        timeBlockFields: this.timeBlockFields,
      },
    });
  }

  getStationLineKeys(): any {
    return Object.keys(this.form.controls || {})
      .filter(key => key.startsWith('line_'))
      .map(key => {
        const group = this.form.get(key) as FormGroup;
        const stationName = group?.get('station')?.value || '';
        return { key, stationName };
      });
  }

  selectStation(station: any) {
    if (this.selectedStationId === station) {
      this.selectedStationId = null;
      this.selectedTimeBlockId = null;
    } else {
      this.selectedStationId = station;
    }

    this.stationFormGroup = this.form.get(station) as FormGroup;
    this.timeKeys = this.getTimeKeys() || [];
  }

  addTime(station: any): void {
    this.selectedStationId = station;
    this.stationFormGroup = this.form.get(station) as FormGroup;

    const now = new Date();
    const timeKey = `time_${now.getTime()}`;
    const newTimeGroup = new FormGroup({});
    let timeValue = '';

    this.timeBlockFields.forEach((field: any) => {
      const isDisabled = !!field?.display_only;
      const isRequired = field?.is_optional === false;
      const validators = isRequired ? [Validators.required] : [];

      const value = field.name === 'time' ? this.formatTimeToAMPM(now) : null;

      timeValue = this.formatTimeToAMPM(now);

      newTimeGroup.addControl(
        field.name,
        new FormControl({ value, disabled: isDisabled }, validators),
      );
    });

    // tslint:disable-next-line:variable-name
    let am_pm = '';
    let timePeriod = '';

    if (timeValue) {
      const [timePart, meridian] = timeValue.split(' ');
      timePeriod = timePart;
      am_pm = meridian;
    }

    newTimeGroup.addControl(
      'timePeriod',
      new FormControl({ value: timePeriod, disabled: false }),
    );

    newTimeGroup.addControl(
      'am_pm',
      new FormControl({ value: am_pm, disabled: false }),
    );

    newTimeGroup.addControl(
      'pictures',
      new FormControl({ value: JSON.stringify([]), disabled: false }),
    );

    newTimeGroup.addControl(
      'videos',
      new FormControl({ value: JSON.stringify([]), disabled: false }),
    );

    this.stationFormGroup.addControl(timeKey, newTimeGroup);

    this.timeKeys = this.getTimeKeys() || [];
    this.selectTimeBlock(timeKey);
  }

  getTimeKeys(): any {
    return Object.keys(this.stationFormGroup.controls || {})
      .filter(key => key.startsWith('time_'))
      .map(key => {
        const group = this.stationFormGroup.get(key) as FormGroup;
        const timeLabel = group?.get('time')?.value || '';
        return { key, timeLabel };
      });
  }

  formatTimeToAMPM(date: Date): string {
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';

    hours = hours % 12;
    hours = hours ? hours : 12; // 0 becomes 12

    const hoursStr = hours < 10 ? '0' + hours : hours;
    const minutesStr = minutes < 10 ? '0' + minutes : minutes;

    return `${hoursStr}:${minutesStr} ${ampm}`;
  }

  deleteStationItem(station: any) {
    this.form.removeControl(station, { emitEvent: true });
    this.stationLineKeys = this.stationLineKeys.filter(o => o.key !== station);
    this.cdRef.detectChanges();
  }

  selectTimeBlock(tk: any) {
    this.selectedTimeBlockId = this.selectedTimeBlockId === tk ? null : tk;

    this.timeFormGroup = this.stationFormGroup.get(tk) as FormGroup;

    const tempPic = this.getPictures(this.timeFormGroup) || [];
    const tempVideo = this.getVideos(this.timeFormGroup) || [];

    this.selectedMediaFiles = [
      ...tempPic,
      ...tempVideo,
    ];

    this.timeBlockFields.forEach((field: any) => {
      if (field?.dependent_fields?.length) {
        this.timeFormGroup.controls[field.name].valueChanges
          .pipe(debounceTime(200), takeUntil(this.onDestroy))
          .subscribe(() => {
            const dependentFields = field.dependent_fields;

            dependentFields.forEach(o => {
              const rawFormula = this.exprEvalConfigExpressions[o.expression_name];
              const formula = rawFormula?.replace(/\s+/g, ' ').trim();
              if (!formula || typeof formula !== 'string') {
                return;
              }

              try {
                const variableNames = [];
                const formulaWords = formula.match(/[a-zA-Z_][a-zA-Z0-9_]*/g) || [];
                let formControlNames = [];
                const timeFormGroupControlName = Object.keys(this.timeFormGroup.controls);
                const headerGroupControlName = this.headerFields.map((hf: any) => hf?.name);

                formControlNames = [
                  ...timeFormGroupControlName,
                  ...headerGroupControlName,
                ];

                formulaWords.forEach(word => {
                  if (formControlNames.includes(word)) {
                    variableNames.push(word);
                  }
                });

                let canEvaluate = true;
                const formValues: any = {};

                variableNames.forEach(varName => {
                  const value = this.timeFormGroup?.controls?.[varName]?.value || this.form?.controls?.[varName]?.value;

                  if (value === null || value === undefined || value === '') {
                    canEvaluate = false;
                  } else {
                    const numericValue = Number(value);
                    if (isNaN(numericValue)) {
                      canEvaluate = false;
                    } else {
                      formValues[varName] = numericValue;
                    }
                  }
                });


                if (!canEvaluate) {
                  return;
                }

                const result = this.evaluator.evaluateExpression(formula, formValues);
                const currentVal = this.timeFormGroup.controls[o.name].value;

                if (currentVal !== result) {
                  this.timeFormGroup.controls[o.name].setValue(result, { emitEvent: false });
                }
              } catch (err) {
                console.error(err);
              }

            });
          });
      }
    });
  }

  deleteTime(tk: any) {
    const stationGroup = this.form.get(this.selectedStationId) as FormGroup;

    if (stationGroup && stationGroup.contains(tk)) {
      stationGroup.removeControl(tk, { emitEvent: true });
    }

    this.timeKeys = this.timeKeys.filter(o => o.key !== tk);

    this.cdRef.detectChanges();
  }

  rebuildFormFromSavedData(): void {
    if (!this.data) {
      return;
    }

    const controlFormId = this.data?.control_form?.id;
    const formTypeDetails = this.resolveFormTemplate(controlFormId);
    if (!formTypeDetails) {
      return;
    }
    // The line/station sections are gated on the formType control having a value.
    // A saved record gets that from the dropdown; a preview has no dropdown, so
    // set it here — without emitting, which would reset the rebuild below.
    if (this.isPreview) {
      this.form?.controls?.['formType']?.setValue(controlFormId, { emitEvent: false });
    }
    this.uiConfig = formTypeDetails?.ui_config;
    this.displaySignature = !!this.uiConfig?.display_signatures;
    this.enableNestedAccordion = !!(this.uiConfig.show_station && this.uiConfig.show_time);
    this.headerFields = this.uiConfig?.header?.fields || [];
    this.footerFields = this.uiConfig?.footer?.fields || [];
    this.lineDetails = this.uiConfig?.lines;

    if (!this.enableNestedAccordion) {
      this.lineFields = this.lineDetails?.fields || [];
    }

    this.averageField = this.lineDetails?.fields?.find(o => o.name === this.lineDetails?.summary?.average_field);

    const fields = this.lineDetails?.fields || [];

    this.stationBlockFields = fields?.filter(field => this.lineDetails?.group_by?.includes(field?.name));
    this.timeBlockFields = fields?.filter(field => !this.lineDetails?.group_by?.includes(field?.name));

    this.createPicklistOptions();

    this.form.patchValue({ formType: controlFormId }, { emitEvent: false });
    this.form.controls['formType'].disable({ emitEvent: false });

    this.headerFields.forEach((field: any) => {
      if (field.type === 'time') {
        this.form?.addControl(
          field?.name,
          new FormControl({ value: null, disabled: !!field?.display_only },
            field?.is_optional === false ? [Validators.required] : [],
          ),
        );

        this.form?.addControl(
          `${field.name}_timePeriod`,
          new FormControl({ value: null, disabled: false }),
        );

        this.form?.addControl(
          `${field.name}_am_pm`,
          new FormControl({ value: null, disabled: false }),
        );
      } else {
        this.form?.addControl(
          field?.name,
          new FormControl({ value: null, disabled: !!field?.display_only },
            field?.is_optional === false ? [Validators.required] : [],
          ),
        );
      }
    });

    this.footerFields.forEach(field => {
      this.form.addControl(
        field.name,
        new FormControl({ value: null, disabled: !!field.display_only },
          field.is_optional === false ? [Validators.required] : []),
      );
    });

    const patchObject: any = {};
    const fieldValues = this.data?.field_values || [];

    [...this.headerFields, ...this.footerFields].forEach((field) => {
      const matchedValue = fieldValues.find((fv: any) => fv?.id === field?.id);
      if (matchedValue) {
        const val = matchedValue.value;

        let finalValue: any;

        if (Array.isArray(val)) {
          finalValue = val;
        } else if (val && typeof val === 'object') {
          finalValue = val.value ?? val.id ?? '';
        } else {
          finalValue = val ?? '';
        }

        if (field.type === 'time' && typeof finalValue === 'string') {
          const amPmRegex = /^(0?[1-9]|1[0-2]):[0-5]\d\s?(AM|PM)$/i; // hh:mm AM/PM format

          if (/^\d{2}:\d{2}:\d{2}$/.test(finalValue)) {
            // HH:MM:SS (24-hour) → convert to hh:mm AM/PM
            const [hourStr, minuteStr] = finalValue.split(':');
            let hour = parseInt(hourStr, 10);
            // tslint:disable-next-line:no-shadowed-variable variable-name
            const am_pm = hour >= 12 ? 'PM' : 'AM';
            hour = hour % 12 || 12;
            finalValue = `${hour.toString().padStart(2, '0')}:${minuteStr} ${am_pm}`;
          } else if (amPmRegex.test(finalValue)) {
            // Already hh:mm AM/PM → do nothing
          } else if (/^\d{2}:\d{2}$/.test(finalValue)) {
            // HH:MM (24-hour) → convert to hh:mm AM/PM
            const [hourStr, minuteStr] = finalValue.split(':');
            let hour = parseInt(hourStr, 10);
            // tslint:disable-next-line:no-shadowed-variable variable-name
            const am_pm = hour >= 12 ? 'PM' : 'AM';
            hour = hour % 12 || 12;
            finalValue = `${hour.toString().padStart(2, '0')}:${minuteStr} ${am_pm}`;
          }

          // Now split into timePeriod + am/pm
          // tslint:disable-next-line:variable-name
          const [timePeriod = '', am_pm = ''] = finalValue.split(' ');
          patchObject[`${field.name}_timePeriod`] = timePeriod;
          patchObject[`${field.name}_am_pm`] = am_pm;
        }

        patchObject[field.name] = finalValue;
      }
    });

    this.form.patchValue(patchObject);

    this.headerFields.forEach((field: any) => {
      if (field?.dependent_fields?.length) {
        const currentVal = this.form.controls[field?.name]?.value;

        if (currentVal) {
          const options = this.picklistOptions[field?.name];
          const selectedOption = options?.find(o => o.value === currentVal);
          const extraAttrs = selectedOption?.extra_attributes || {};

          Object.entries(extraAttrs).forEach(([key, value]: any) => {
            field?.dependent_fields?.forEach(dep => {
              if (dep?.extra_attribute === key) {
                const controlKey = dep.name;
                const control = this.form.get(controlKey);
                if (control) {
                  control.setValue(value, { emitEvent: false });
                }
              }
            });
          });
        }
      }

      if (field?.allow_other) {
        if (this.form.controls[field.name].value === '_others') {
          this.form.addControl(
            `${field.name}_others`,
            new FormControl({ value: null, disabled: false }),
          );
        } else {
          this.form.removeControl(`${field.name}_others`);
        }

        this.form.controls[field.name].valueChanges
          .pipe(takeUntil(this.onDestroy))
          .subscribe(d => {
            if (d === '_others') {
              this.form.addControl(
                `${field.name}_others`,
                new FormControl({ value: null, disabled: false }),
              );
            } else {
              this.form.removeControl(`${field.name}_others`);
            }
          });
      }
    });

    this.form.patchValue({
      latitude: this.data?.latitude,
      longitude: this.data?.longitude,
      map_feature_link: this.data?.map_feature_link ?? null,
      comment: this.data?.comment,
      pictures: this.data?.pictures ? JSON.stringify(this.data.pictures) : JSON.stringify([]),
      videos: this.data?.videos ? JSON.stringify(this.data.videos) : JSON.stringify([]),
    });

    this.mediaFiles = [...this.data?.pictures || [], ...this.data?.videos || []];
    this.displayCommentSection = !!this.data?.comment;

    if (this.data?.signatures?.length) {
      this.signatures = this.data.signatures;
      this.refreshSignatureViews();
    }

    if (this.enableNestedAccordion) {
      const groupedStations: { [key: string]: any[] } = {};

      this.data.lines.forEach((line: any) => {
        const stationValues = line.field_values.filter((fv: any) =>
          this.stationBlockFields.find((sf: any) => sf.id === fv.id),
        );
        const stationKey = stationValues.map((fv: any) => `${fv.id}:${fv.value}`).join('|');

        if (!groupedStations[stationKey]) {
          groupedStations[stationKey] = [];
        }

        groupedStations[stationKey].push(line);
      });

      let stationIndex = 0;
      for (const [_, lines] of Object.entries(groupedStations)) {
        const stationFormKey = `line_${stationIndex}`;
        const stationGroup = new FormGroup({});

        // Add station-level fields
        this.stationBlockFields.forEach((sf) => {
          const matchedVal = lines[0].field_values.find((fv: any) => fv.id === sf.id);
          const value = typeof matchedVal?.value === 'object' ? matchedVal?.value?.value : matchedVal?.value ?? null;

          stationGroup.addControl(
            sf.name,
            new FormControl({ value, disabled: !!sf?.display_only },
              sf?.is_optional === false ? [Validators.required] : []),
          );
        });

        // Add each line as a time block
        lines.forEach((line: any, tIndex: number) => {

          const timeKey = `time_${tIndex}`;
          const timeGroup = new FormGroup({});

          this.timeBlockFields.forEach((tbf: any) => {
            const matchedVal = line.field_values.find((fv: any) => fv.id === tbf.id);
            let value = typeof matchedVal?.value === 'object'
              ? matchedVal?.value?.value
              : matchedVal?.value ?? null;

            // If it's a time-like field
            if (/_time|time/i.test(tbf.name) && typeof value === 'string') {
              const amPmRegex = /^(0?[1-9]|1[0-2]):[0-5]\d\s?(AM|PM)$/i;

              if (/^\d{2}:\d{2}:\d{2}$/.test(value)) {
                // HH:MM:SS → HH:MM AM/PM
                const [hourStr, minuteStr] = value.split(':');
                let hour = parseInt(hourStr, 10);
                // tslint:disable-next-line:variable-name
                const am_pm = hour >= 12 ? 'PM' : 'AM';
                hour = hour % 12 || 12;
                value = `${hour.toString().padStart(2, '0')}:${minuteStr} ${am_pm}`;
              } else if (/^\d{2}:\d{2}$/.test(value) && !amPmRegex.test(value)) {
                // HH:MM → HH:MM AM/PM
                const [hourStr, minuteStr] = value.split(':');
                let hour = parseInt(hourStr, 10);
                // tslint:disable-next-line:variable-name
                const am_pm = hour >= 12 ? 'PM' : 'AM';
                hour = hour % 12 || 12;
                value = `${hour.toString().padStart(2, '0')}:${minuteStr} ${am_pm}`;
              }
              // If already AM/PM, do nothing
            }

            if (tbf.name === 'time') {
              let timePeriodVal = '';
              let amPmVal = '';

              if (typeof value === 'string') {
                const [timePart, ampmPart] = value.split(' ');
                amPmVal = ampmPart || '';

                const [hour, minute] = timePart.split(':');
                const paddedHour = hour.padStart(2, '0');
                const paddedMinute = minute.padStart(2, '0');
                timePeriodVal = `${paddedHour}:${paddedMinute}`;
              }

              const formattedTime = `${timePeriodVal}${amPmVal ? ' ' + amPmVal : ''}`;

              timeGroup.addControl(
                'time',
                new FormControl(
                  { value: formattedTime, disabled: !!tbf?.display_only },
                  tbf?.is_optional === false ? [Validators.required] : [],
                ),
              );

              timeGroup.addControl('timePeriod', new FormControl({ value: timePeriodVal, disabled: false }));
              timeGroup.addControl('am_pm', new FormControl({ value: amPmVal, disabled: false }));
            } else {
              timeGroup.addControl(
                tbf.name,
                new FormControl(
                  { value, disabled: !!tbf?.display_only },
                  tbf?.is_optional === false ? [Validators.required] : [],
                ),
              );
            }

            timeGroup.addControl('pictures', new FormControl({ value: JSON.stringify(line?.pictures), disabled: false }));
            timeGroup.addControl('videos', new FormControl({ value: JSON.stringify(line?.videos), disabled: false }));
          });

          this.evaluateTimeBlockFormulas(timeGroup);

          stationGroup.addControl(timeKey, timeGroup);
        });

        this.form.addControl(stationFormKey, stationGroup);
        stationIndex++;
      }

      this.stationLineKeys = this.getStationLineKeys() || [];
      this.selectedStationId = null;
      this.selectedTimeBlockId = null;

      this.calculateStationAverageFieldMap();
    } else {
      this.data?.lines.forEach((line: any, lIndex: number) => {

        const lineKey = `line_${lIndex + 1}`;
        const lineGroup = new FormGroup({});

        this.lineFields.forEach((lf: any) => {
          const matchedVal = line.field_values.find((fv: any) => fv.id === lf.id);
          let value = typeof matchedVal?.value === 'object'
            ? matchedVal?.value?.value
            : matchedVal?.value ?? null;

          if (/_time|time/i.test(lf.name) && typeof value === 'string') {
            const amPmRegex = /^(0?[1-9]|1[0-2]):[0-5]\d\s?(AM|PM)$/i;

            if (/^\d{2}:\d{2}:\d{2}$/.test(value)) {
              // HH:MM:SS → hh:mm AM/PM
              const [hourStr, minuteStr] = value.split(':');
              let hour = parseInt(hourStr, 10);
              // tslint:disable-next-line:variable-name
              const am_pm = hour >= 12 ? 'PM' : 'AM';
              hour = hour % 12 || 12;
              value = `${hour.toString().padStart(2, '0')}:${minuteStr} ${am_pm}`;
            } else if (/^\d{2}:\d{2}$/.test(value) && !amPmRegex.test(value)) {
              // HH:MM → hh:mm AM/PM
              const [hourStr, minuteStr] = value.split(':');
              let hour = parseInt(hourStr, 10);
              // tslint:disable-next-line:variable-name
              const am_pm = hour >= 12 ? 'PM' : 'AM';
              hour = hour % 12 || 12;
              value = `${hour.toString().padStart(2, '0')}:${minuteStr} ${am_pm}`;
            }
            // If already AM/PM, do nothing
          }

          if (lf?.name === 'time' || lf?.name?.includes('time')) {
            let timePeriodVal = '';
            let amPmVal = '';

            if (typeof value === 'string') {
              const [timePart, ampmPart] = value.split(' ');
              amPmVal = ampmPart || '';

              const [hour, minute] = timePart.split(':');
              const paddedHour = hour.padStart(2, '0');
              const paddedMinute = minute.padStart(2, '0');
              timePeriodVal = `${paddedHour}:${paddedMinute}`;
            }

            const formattedTime = `${timePeriodVal}${amPmVal ? ' ' + amPmVal : ''}`;

            lineGroup.addControl(
              lf?.name === 'time' ? 'time' : lf?.name?.includes('time') ? lf?.name : '',
              new FormControl(
                { value: formattedTime, disabled: !!lf?.display_only },
                lf?.is_optional === false ? [Validators.required] : [],
              ),
            );

            lineGroup.addControl(
              lf?.name === 'time' ? 'timePeriod' : lf?.name?.includes('time') ? lf?.name + '_timePeriod' : '',
              new FormControl({
                value: timePeriodVal,
                disabled: false,
              }));

            lineGroup.addControl(
              lf?.name === 'time' ? 'am_pm' : lf?.name?.includes('time') ? lf?.name + '_am_pm' : '',
              new FormControl({
                value: amPmVal,
                disabled: false,
              }));
          } else {
            lineGroup.addControl(
              lf.name,
              new FormControl(
                { value, disabled: !!lf?.display_only },
                lf?.is_optional === false ? [Validators.required] : [],
              ),
            );
          }

          lineGroup.addControl('pictures', new FormControl({ value: JSON.stringify(line?.pictures), disabled: false }));
          lineGroup.addControl('videos', new FormControl({ value: JSON.stringify(line?.videos), disabled: false }));
        });

        this.form.addControl(lineKey, lineGroup);

        this.lineKeys = this.getLineKeys() || [];
      });
    }
  }

  calculateStationAverageFieldMap(): void {
    if (!this.averageField?.name) {
      return;
    }

    let dependentField: any;

    this.timeBlockFields.forEach(tbf => {
      if (tbf.dependent_fields) {
        const match = tbf.dependent_fields?.find(df => df.name === this.averageField.name);
        if (match) {
          dependentField = match;
        }
      }
    });

    if (!dependentField?.expression_name) {
      return;
    }

    const formula = this.exprEvalConfigExpressions[dependentField.expression_name]?.trim();
    if (!formula) {
      return;
    }

    this.stationAverageFieldMap = {};

    Object.keys(this.form.controls)
      .filter(key => key.startsWith('line_'))
      .forEach(stationKey => {
        const stationGroup = this.form.get(stationKey) as FormGroup;
        const averageFieldMap: Record<string, number | null> = {};
        let total = 0;
        let count = 0;

        Object.keys(stationGroup.controls)
          .filter(key => key.startsWith('time_'))
          .forEach(timeKey => {
            const timeGroup = stationGroup.get(timeKey) as FormGroup;
            const blockValue = timeGroup?.getRawValue?.() || {};
            const formValue = this.form.getRawValue();
            const fullValue = { ...blockValue, ...formValue };

            const formulaWords = formula.match(/[a-zA-Z_][a-zA-Z0-9_]*/g) || [];
            const filteredValue: Record<string, any> = {};

            formulaWords.forEach(word => {
              if (fullValue.hasOwnProperty(word)) {
                filteredValue[word] = fullValue[word];
              }
            });

            try {
              const result = this.evaluator.evaluateExpression(formula, filteredValue);
              const numericResult = !isNaN(result) ? result : null;
              averageFieldMap[timeKey] = numericResult;

              if (typeof numericResult === 'number') {
                total += numericResult;
                count++;
              }
            } catch (err) {
              averageFieldMap[timeKey] = null;
            }
          });

        this.stationAverageFieldMap[stationKey] = {
          averageFieldMap,
          average: count > 0 ? total / count : null,
        };
      });
  }

  getForm(enableNestedAccordion: any, selectedStationId: any, selectedTimeblockId: any, selectedLineId: any): FormGroup {
    if (enableNestedAccordion) {
      if (!selectedStationId || !selectedTimeblockId) {
        return this.form;
      }

      const stationForm = this.form.get(selectedStationId) as FormGroup | null;
      const timeBlockForm = stationForm?.get(selectedTimeblockId) as FormGroup | null;

      return timeBlockForm ?? this.form;
    } else {
      if (!selectedLineId) {
        return this.form;
      }

      const lineForm = this.form.get(selectedLineId) as FormGroup | null;

      return lineForm ?? this.form;
    }
  }

  onToggle($event: any, controlName: any) {
    this.form.controls[controlName].setValue($event.checked);
  }

  addEntry() {
    let index = 1;
    let lineKey = `line_${index}`;
    while (this.form.contains(lineKey)) {
      index++;
      lineKey = `line_${index}`;
    }
    const newLineGroup = new FormGroup({});

    this.lineFields.forEach((field: any) => {
      const isDisabled = !!field?.display_only;
      const isRequired = field?.is_optional === false;
      const validators = isRequired ? [Validators.required] : [];

      if (field.type === 'time') {
        newLineGroup?.addControl(
          field?.name,
          new FormControl({ value: null, disabled: isDisabled }, validators),
        );

        newLineGroup?.addControl(
          `${field.name}_timePeriod`,
          new FormControl({ value: null, disabled: false }),
        );

        newLineGroup?.addControl(
          `${field.name}_am_pm`,
          new FormControl({ value: null, disabled: false }),
        );
      } else {
        newLineGroup?.addControl(
          field?.name,
          new FormControl({ value: null, disabled: isDisabled }, validators),
        );
      }
    });

    newLineGroup.addControl(
      'pictures',
      new FormControl({ value: JSON.stringify([]), disabled: false }),
    );

    newLineGroup.addControl(
      'videos',
      new FormControl({ value: JSON.stringify([]), disabled: false }),
    );

    this.form.addControl(lineKey, newLineGroup);

    this.lineKeys = this.getLineKeys() || [];
    this.selectLine(lineKey);
  }

  selectLine(key: any) {
    this.selectedLineId = this.selectedLineId === key ? null : key;

    this.lineFormGroup = this.form.get(key) as FormGroup;

    const tempPic = this.getPictures(this.lineFormGroup) || [];
    const tempVideo = this.getVideos(this.lineFormGroup) || [];

    this.selectedMediaFiles = [
      ...tempPic,
      ...tempVideo,
    ];
  }

  deleteLineEntry(key: any) {
    this.form.removeControl(key, { emitEvent: true });
    this.lineKeys = this.lineKeys.filter(o => o.key !== key);
    this.cdRef.detectChanges();
  }

  getLineKeys() {
    this.lineKeys = [];
    return Object.keys(this.form.controls || {})
      .filter(key => key.startsWith('line_'))
      .map(key => {
        return { key, lineName: this.lineFields?.find(o => o?.display_order === 1)?.label };
      });
  }

  resetToInitialState() {
    const currentKeys = Object.keys(this.form.controls);
    const keysToRemove = currentKeys.filter(key => !this.initialFormKeys.includes(key));

    keysToRemove.forEach(key => {
      this.form.removeControl(key);
    });
  }

  /**
   * Role options for the signature dialog, sourced from the form's
   * ui_config.signature_roles (QA/QC fallback). Roles may repeat across signers.
   * Shared with control-forms-details via the buildSignatureRoleOptions helper.
   */
  getSignatureRoleOptions(): { label: string; value: string }[] {
    return buildSignatureRoleOptions(this.uiConfig);
  }

  /**
   * Open the shared draw-only signature capture dialog. `index` targets an
   * existing signer slot for editing; pass null/omit to append a new signer.
   */
  openSignatureDetails(index: number | null = null) {
    if (this.isPreview) {
      this.previewSignature.emit();
      return;
    }
    if (this.dialogsSuppressed) {
      return;
    }

    this.dialog.open(SignatureComponent, {
      disableClose: true,
      width: '580px',
      maxHeight: '90vh',
      data: {
        index,
        signer: index != null ? this.signatures?.[index] : null,
        form: this.form,
        options: this.getSignatureRoleOptions(),
        // Capture mode: when true the dialog uses the draw pad; otherwise it
        // captures a typed signature name. Defaults to false (typed).
        signaturePadEnabled: !!this.uiConfig?.signature_pad?.enabled,
        // Per-form gate (draw-pad mode only): when true the printed name is
        // mandatory. Defaults to false when the template doesn't configure it.
        requirePrintedName: !!this.uiConfig?.signature_pad?.require_printed_name,
      },
    });
  }

  /** Remove a captured signer by index, then rebuild the display models. */
  removeSignature(index: number) {
    if (index < 0 || index >= (this.signatures?.length ?? 0)) {
      return;
    }
    this.signatures.splice(index, 1);
    this.refreshSignatureViews();
    this.cdRef.detectChanges();
  }

  /**
   * Rebuild the read-only signature presentation models from `this.signatures`.
   * Only signers carrying text or a resolved drawn image are shown. The view
   * keeps its source index so edit/remove target the real array position.
   */
  refreshSignatureViews(): void {
    this.signatureViews = (this.signatures || [])
      .map((s: any, i: number) => ({ view: buildSignatureView(s, undefined, i), raw: s }))
      .filter(({ view }: { view: SignatureView }) => view.text || view.imageUrl)
      .map(({ view }: { view: SignatureView }) => view);
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }

  private evaluateTimeBlockFormulas(timeGroup: FormGroup): void {
    this.timeBlockFields.forEach((field: any) => {
      if (field?.dependent_fields?.length) {
        const dependentFields = field.dependent_fields;

        dependentFields.forEach(o => {
          const rawFormula = this.exprEvalConfigExpressions[o.expression_name];
          const formula = rawFormula?.replace(/\s+/g, ' ').trim();
          if (!formula || typeof formula !== 'string') {
            return;
          }

          try {
            const variableNames = [];
            const formulaWords = formula.match(/[a-zA-Z_][a-zA-Z0-9_]*/g) || [];

            const formControlNames = [
              ...Object.keys(timeGroup.controls),
              ...this.headerFields.map(hf => hf.name),
            ];

            formulaWords.forEach(word => {
              if (formControlNames.includes(word)) {
                variableNames.push(word);
              }
            });

            let canEvaluate = true;
            const formValues: any = {};

            variableNames.forEach(varName => {
              const value =
                timeGroup?.controls?.[varName]?.value ??
                this.form?.controls?.[varName]?.value;

              if (value === null || value === undefined || value === '') {
                canEvaluate = false;
              } else {
                const numericValue = Number(value);
                if (isNaN(numericValue)) {
                  canEvaluate = false;
                } else {
                  formValues[varName] = numericValue;
                }
              }
            });

            if (!canEvaluate) {
              return;
            }

            const result = this.evaluator.evaluateExpression(formula, formValues);
            const currentVal = timeGroup.controls[o.name].value;

            if (currentVal !== result) {
              timeGroup.controls[o.name].setValue(result, { emitEvent: false });
            }
          } catch (err) {
            console.error(err);
          }
        });
      }
    });

  }
}
